import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { CalendarRange, Plus, Trash2, X } from "lucide-react";
import { listSeasons, createSeason, updateSeason, deleteSeason } from "@/lib/api";

const BAHARS = ["mrig", "hasta", "ambia", "other"];

export default function AdminSeasons() {
  const { t } = useTranslation();
  const [items, setItems] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const load = () => listSeasons().then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm(t("admin.confirm_delete"))) return;
    try { await deleteSeason(id); load(); toast.success(t("toast.deleted")); }
    catch (err) { toast.error(err?.response?.data?.detail?.error || t("toast.failed")); }
  };
  const toggleActive = async (s) => {
    try { await updateSeason(s.id, { is_active: !s.is_active }); load(); } catch { toast.error(t("toast.failed")); }
  };

  return (
    <div data-testid="admin-seasons">
      <div className="flex items-center gap-3 mb-2">
        <CalendarRange className="w-6 h-6 text-[var(--tr-deep-green)]" />
        <h2 className="tr-section-title text-3xl">{t("seasons.title")}</h2>
      </div>
      <p className="text-[var(--tr-muted)] leading-relaxed max-w-3xl">{t("seasons.body")}</p>
      <div className="mt-6 flex gap-2">
        <button data-testid="season-new-btn" onClick={() => setShowNew(true)} className="tr-pill-btn tr-btn-primary"><Plus className="w-4 h-4" /> {t("seasons.new_season")}</button>
      </div>
      {!items ? <div className="p-6 text-[var(--tr-muted)]">{t("common.loading")}</div>
        : items.length === 0 ? <div data-testid="seasons-empty" className="tr-card p-10 mt-6 text-center text-[var(--tr-muted)]">{t("seasons.empty")}</div>
        : (
          <div className="tr-card mt-6 overflow-hidden" data-testid="seasons-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[var(--tr-sage)]/50"><tr>
                  {[t("seasons.col_label"), t("seasons.col_year"), t("seasons.col_bahar"), t("seasons.col_range"), t("seasons.col_active")].map((h) => (
                    <th key={h} className="text-left px-4 py-3 font-semibold text-[var(--tr-deep-green)] uppercase text-[10px] tracking-[0.18em]">{h}</th>))}
                  <th className="text-right px-4 py-3 text-[var(--tr-deep-green)] uppercase text-[10px] tracking-[0.18em]">{t("common.actions")}</th>
                </tr></thead>
                <tbody>
                  {items.map((s) => (
                    <tr key={s.id} data-testid={`season-row-${s.id}`} className="border-t border-[rgba(6,78,59,0.08)] hover:bg-[var(--tr-bone)]">
                      <td className="px-4 py-3 font-semibold text-[var(--tr-deep-green)]">{s.label}</td>
                      <td className="px-4 py-3">{s.year}</td>
                      <td className="px-4 py-3 uppercase text-xs">{s.bahar || "—"}</td>
                      <td className="px-4 py-3">{s.start_month ? `${s.start_month} – ${s.end_month || "-"}` : "—"}</td>
                      <td className="px-4 py-3">
                        <button data-testid={`season-active-${s.id}`} onClick={() => toggleActive(s)} className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${s.is_active ? "bg-[var(--tr-lime)]/30 text-[var(--tr-deep-green)]" : "bg-gray-200 text-gray-600"}`}>
                          {s.is_active ? "Active" : "Inactive"}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button data-testid={`season-delete-${s.id}`} onClick={() => remove(s.id)} className="text-red-600 hover:underline text-sm font-semibold"><Trash2 className="w-4 h-4 inline" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      {showNew && <NewSeasonModal onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

function NewSeasonModal({ onClose, onSaved }) {
  const { t } = useTranslation();
  const [f, setF] = useState({ label: "", year: new Date().getFullYear(), bahar: "mrig", start_month: 6, end_month: 12, is_active: true, notes: "" });
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const save = async () => {
    if (!f.label || !f.year) { toast.error(t("toast.fill_required")); return; }
    try {
      await createSeason({ ...f, year: parseInt(f.year), start_month: f.start_month ? parseInt(f.start_month) : null, end_month: f.end_month ? parseInt(f.end_month) : null });
      toast.success(t("toast.saved")); onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail?.error || t("toast.failed")); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-6" data-testid="season-modal">
      <div className="tr-card max-w-lg w-full p-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display font-extrabold text-2xl text-[var(--tr-deep-green)]">{t("seasons.new_season")}</h3>
          <button onClick={onClose} data-testid="season-modal-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="block md:col-span-2"><span className="tr-label">{t("seasons.f_label")}*</span><input data-testid="season-label" className="tr-input" value={f.label} onChange={set("label")} /></label>
          <label className="block"><span className="tr-label">{t("seasons.f_year")}*</span><input type="number" data-testid="season-year" className="tr-input" value={f.year} onChange={set("year")} /></label>
          <label className="block"><span className="tr-label">{t("seasons.f_bahar")}</span>
            <select data-testid="season-bahar" className="tr-input" value={f.bahar} onChange={set("bahar")}>
              {BAHARS.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </label>
          <label className="block"><span className="tr-label">{t("seasons.f_start")}</span><input type="number" min="1" max="12" data-testid="season-start" className="tr-input" value={f.start_month} onChange={set("start_month")} /></label>
          <label className="block"><span className="tr-label">{t("seasons.f_end")}</span><input type="number" min="1" max="12" data-testid="season-end" className="tr-input" value={f.end_month} onChange={set("end_month")} /></label>
          <label className="block md:col-span-2"><span className="tr-label">{t("seasons.f_notes")}</span><textarea data-testid="season-notes" className="tr-input tr-textarea" value={f.notes} onChange={set("notes")} /></label>
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="tr-pill-btn tr-btn-outline">{t("common.cancel")}</button>
          <button data-testid="season-save" onClick={save} className="tr-pill-btn tr-btn-primary">{t("seasons.save")}</button>
        </div>
      </div>
    </div>
  );
}
